/**
 * Copyright © 2026 Adnuntius AS.
 */
import {AdnListHelperService} from '../common/controller/list-helper.service';
import {ChangeDetectorRef, Component, Inject, Input, NgZone, OnChanges, OnDestroy, SimpleChanges} from '@angular/core';
import {deferMarkForCheck} from '../common/util/defer-mark-for-check';
import * as _ from 'lodash';
import {TranslateService} from '@ngx-translate/core';
import template from './segment-side-panel.component.html';
import {triggerConditionFormatter} from '../trigger/trigger-util';
import {AngularModalService} from '../common/modal/angular-modal.service';
import {SegmentTriggerModalComponent} from './segment-trigger-modal.component';
import {TriggerResource} from '../../components/api/resources/resource/trigger.resource';
import {LocalNetworkProfile} from '../../components/session/local-network-profile';

@Component({
  standalone: false,
  selector: 'adn-segment-side-panel',
  template
})
export class SegmentSidePanelComponent implements OnChanges, OnDestroy {
  @Input() model: any;
  @Input() modelComms: any;
  @Input() isNew: boolean;

  effectiveIsNew = false;
  activeTab: 'triggers' | 'charts' = 'triggers';
  hasAdvertising = false;
  showingCharts = false;
  selFolder: any;

  triggersList: any = {};
  /** Snapshot — list-helper replaces triggersList.items on settle (NG0100). */
  displayItems: any[] = [];
  hasItems = false;

  private subscribedModelComms: any;

  constructor(
    @Inject(TriggerResource) private readonly Trigger: any,
    private readonly adnListHelper: AdnListHelperService,
    @Inject(LocalNetworkProfile) private readonly LocalNetworkProfile: any,
    private readonly translate: TranslateService,
    private readonly angularModalService: AngularModalService,
    private readonly ngZone: NgZone,
    private readonly cdr: ChangeDetectorRef
  ) {
    this.hasAdvertising = this.LocalNetworkProfile.isAdvertisingPlatform();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.isNew) {
      this.effectiveIsNew = !!this.isNew;
    }
    if (changes.model) {
      this.syncTriggersList();
      this.selFolder = _.get(this.model, ['folderMembers', 0]);
    }
    if (changes.modelComms) {
      this.subscribeModelComms();
    }
  }

  ngOnDestroy(): void {
    this.subscribedModelComms = null;
  }

  selectTab(tab: 'triggers' | 'charts'): void {
    this.activeTab = tab;
    if (tab === 'charts') {
      this.showingCharts = true;
    }
  }

  triggerFormatter(entry: any): string[] {
    return triggerConditionFormatter(this.translate, entry);
  }

  doTrigger(entry?: any): void {
    const modalRef = this.angularModalService.show(SegmentTriggerModalComponent, {
      class: 'modal-lg adnDialog',
      initialState: {
        segmentModel: this.model,
        entry
      }
    });

    const modalContent = modalRef.content as SegmentTriggerModalComponent;
    modalContent.onClosed.subscribe(() => {
      this.ngZone.run(() => {
        this.triggersList.makeQuery?.();
      });
    });
    modalRef.onHidden?.subscribe(() => {
      this.ngZone.run(() => {
        this.triggersList.makeQuery?.();
      });
    });
  }

  private syncTriggersList(): void {
    if (this.effectiveIsNew || !this.model?.id) {
      this.displayItems = [];
      this.hasItems = false;
      return;
    }

    this.displayItems = [];
    this.hasItems = false;
    this.adnListHelper.setUpBasicList(this.triggersList, this.Trigger, 'fullTriggersForSegment', {
      moreParams: {segment: this.model.id},
      pageSize: 101,
      afterFill: () => {
        deferMarkForCheck(this.cdr, () => {
          this.displayItems = (this.triggersList.items || []).slice();
          this.hasItems = this.displayItems.length > 0;
        });
      }
    });
  }

  private subscribeModelComms(): void {
    if (!this.modelComms || this.subscribedModelComms === this.modelComms) {
      return;
    }
    this.subscribedModelComms = this.modelComms;
    this.modelComms.addSubscriber((data: any, type: any, isNewFromComms: boolean) => {
      if (type) {
        return;
      }
      this.ngZone.run(() => {
        this.effectiveIsNew = isNewFromComms;
        if (!this.effectiveIsNew && this.model?.id) {
          this.syncTriggersList();
        }
      });
    });
  }
}
